"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Building2, Plus } from "lucide-react";
import { useUpdateContact } from "@/features/contacts/hooks";
import { Client } from "@/lib/types/database";
import { useToast } from "@/components/ui/toast";

interface QuickCompanyCreateProps {
  contact: Client;
  onCompanyAdded?: (companyName: string) => void;
}

export const QuickCompanyCreate: React.FC<QuickCompanyCreateProps> = ({
  contact,
  onCompanyAdded,
}) => {
  const [companyName, setCompanyName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const updateContactMutation = useUpdateContact();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!companyName.trim()) {
      toast({
        type: "error",
        title: "Error",
        description: "Please enter a company name",
      });
      return;
    }

    setIsLoading(true);

    try {
      await updateContactMutation.mutateAsync({
        id: contact.id,
        updates: {
          company_name: companyName.trim(),
        },
      });

      toast({
        type: "success",
        title: "Success",
        description: `Company "${companyName.trim()}" has been added to this contact`,
      });

      // Call the callback to refresh the parent component
      onCompanyAdded?.(companyName.trim());

      // Clear the input
      setCompanyName("");
    } catch (error) {
      toast({
        type: "error",
        title: "Error",
        description: "Failed to add company. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="space-y-4">
          <div className="flex items-center gap-3 text-muted-foreground">
            <Building2 className="h-5 w-5" />
            <div>
              <h3 className="font-medium text-foreground">No Company Associated</h3>
              <p className="text-sm">
                Add a company name to this contact to unlock company features like viewing
                colleagues and adding team members.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company-name">Company Name</Label>
              <Input
                id="company-name"
                type="text"
                placeholder="Enter company name..."
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <Button type="submit" disabled={isLoading || !companyName.trim()} className="gap-2">
              <Plus className="h-4 w-4" />
              {isLoading ? "Adding Company..." : "Add Company"}
            </Button>
          </form>
        </div>
      </CardContent>
    </Card>
  );
};
